/**
 * Tiny event batcher that POSTs to /api/debug-log on the server.
 *
 * Lives outside the audio engine so call-sites can fire-and-forget. A
 * page-scoped sessionId ties events across multiple takes together so
 * we can compare take 1 vs take 2 vs take 3.
 *
 * Use this for the "first take fades, subsequent takes are clean" iOS
 * audio investigation — record per-chunk RMS/peak from the worklet to
 * see if the fade is in the raw samples (iOS bug) or in our pipeline.
 */

import { getWebApp } from './telegram.js';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** Stable id for the current page load. Logged with every event so the
 *  server-side analysis can group events that belong to one session. */
export const debugSessionId =
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

interface LogEntry {
  e: string;
  t: number;
  s: string;
  [k: string]: unknown;
}

let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let enabled = true;

const t0 = Date.now();

export function setDebugLogEnabled(on: boolean): void {
  enabled = on;
}

/** Fire-and-forget. Use a short `event` string (e.g. "rec:chunk") and
 *  arbitrary additional fields. Numeric values keep precision; objects
 *  are serialised. */
export function debugLog(event: string, data: Record<string, unknown> = {}): void {
  if (!enabled) return;
  buffer.push({ e: event, t: Date.now() - t0, s: debugSessionId, ...data });
  if (!flushTimer) {
    flushTimer = setTimeout(() => { void flushDebugLog(); }, 1000);
  }
}

async function flushDebugLog(): Promise<void> {
  flushTimer = null;
  if (buffer.length === 0) return;
  const batch = buffer;
  buffer = [];
  try {
    const initData = getWebApp()?.initData ?? '';
    await fetch(`${API_BASE}/api/debug-log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Telegram-Init-Data': initData,
      },
      body: JSON.stringify({ batch }),
      keepalive: true,
    });
  } catch (e) {
    console.warn('debug-log flush failed:', e);
  }
}

/** Force an immediate flush — call at the end of a take so the chunks
 *  reach the server before the next take's logs interleave. */
export async function flushDebugLogNow(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  await flushDebugLog();
}
